import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../../utils/axios';

// Enquiries screen shows its own Snackbar → opt mutations out of the global
// duplicate SUCCESS toast; keep global ERROR as the safety net.
const SN = { skipNotify: 'success' };

export const fetchEnquiries = createAsyncThunk(
  'enquiry/fetchEnquiries',
  async (params, { rejectWithValue }) => {
    try {
      const response = await axios.get('/generalResellerEnquiry', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || 'Failed to fetch enquiries');
    }
  }
);

export const updateEnquiryStatus = createAsyncThunk(
  'enquiry/updateStatus',
  async ({ id, is_processed }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`/generalResellerEnquiry/status/${id}`, { is_processed }, SN);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || 'Failed to update status');
    }
  }
);

export const bulkRequeueUnmatched = createAsyncThunk(
  'enquiry/bulkRequeueUnmatched',
  async (payload = {}, { rejectWithValue }) => {
    try {
      const response = await axios.post('/generalResellerEnquiry/bulk-requeue-unmatched', payload, SN);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || 'Failed to requeue unmatched enquiries');
    }
  }
);


// Fill in the service on a Services enquiry the lead cron parked, and requeue
// it. See serviceLeadRouting.js on the backend: without a sub-category the cron
// cannot tell a plumber from a software house, so it holds the enquiry instead
// of fanning it out and burning a paid lead off every unrelated provider.
export const setEnquirySubCategory = createAsyncThunk(
  'enquiry/setSubCategory',
  async ({ id, sub_category_name }, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        `/generalResellerEnquiry/sub-category/${id}`,
        { sub_category_name },
        SN
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || 'Failed to set the service');
    }
  }
);

export const deleteEnquiry = createAsyncThunk(
  'enquiry/delete',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.delete(`/generalResellerEnquiry/${id}`, SN);
      return { id, msg: response.data.msg };
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || 'Failed to delete enquiry');
    }
  }
);

const initialState = {
  enquiries: [],
  totalEnquiries: 0,
  totalPages: 0,
  currentPage: 1,
  isLoading: false,
  error: null,
};

const enquirySlice = createSlice({
  name: 'enquiry',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchEnquiries.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchEnquiries.fulfilled, (state, action) => {
        state.isLoading = false;
        state.enquiries = action.payload.enquiries;
        state.totalEnquiries = action.payload.totalEnquiries;
        state.totalPages = action.payload.totalPages;
        state.currentPage = action.payload.currentPage;
      })
      .addCase(fetchEnquiries.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updateEnquiryStatus.fulfilled, (state, action) => {
        const u = action.payload?.updatedEnquiry;
        if (u?._id) {
          const index = state.enquiries.findIndex(e => e._id === u._id);
          if (index !== -1) {
            state.enquiries[index] = u;
          }
        }
      })
      .addCase(deleteEnquiry.fulfilled, (state, action) => {
        state.enquiries = state.enquiries.filter(e => e._id !== action.payload.id);
        state.totalEnquiries -= 1;
      });
  },
});

export default enquirySlice.reducer;
